/** @jsx jsx */
import { jsx, Box, Flex } from 'theme-ui';
import React, { FunctionComponent } from 'react';
import classNames from 'classnames';

// Components
import CTAButton from '../ctaButton';
import Dropdown, { DropdownOption } from '../dropdown';
import Text, { TEXT_TYPES } from '../text';

// Styles
import { baseStyle } from './style';

export interface NewSelectorOptions {
  label: string;
  value: string;
  url: string;
}

export interface NewsSelectorProps {
  className?: string;
  options: NewSelectorOptions[];
  activePage: string;
}

const allOption: DropdownOption = {
  label: 'All',
  value: 'newsroom',
  url: '/newsroom',
};

const getFirstLetters = function getFirstLetters(inputText: string) {
  return inputText
    .match(/\b(\w)/g)
    ?.map(e => e.toUpperCase())
    .join('');
};

const NewsSelector: FunctionComponent<NewsSelectorProps> = React.memo(
  ({ className, options, activePage }) => {
    // Memos
    const optionsToUse = React.useMemo(() => [allOption, ...options], [
      options,
    ]);

    const optionsToUseMobile = React.useMemo(
      () =>
        optionsToUse.map(o => {
          if (o.label.length > 16) {
            return {
              ...o,
              label: getFirstLetters(o.label),
            };
          }
          return o;
        }),
      [optionsToUse],
    );

    const defaultOption = React.useMemo(
      () => optionsToUse.find(o => o.value === activePage),
      [optionsToUse, activePage],
    );

    return (
      <Box className={classNames('NewsSelector', className)}>
        <Flex sx={baseStyle.containerDesktop}>
          {optionsToUse.map(option => (
            <CTAButton
              key={option.value}
              type="link"
              cta={{
                url: option.url,
                label: option.label,
                target: '_self',
              }}
              alternative
              sx={baseStyle.desktopButton}
              className={activePage === option.value ? 'selected' : ''}
            >
              <Text type={TEXT_TYPES.AUTOFILL} sx={baseStyle.tagContent}>
                {option.label}
              </Text>
            </CTAButton>
          ))}
        </Flex>
        <Flex sx={baseStyle.containerMobile}>
          <Dropdown
            label={defaultOption?.label || 'All'}
            options={optionsToUseMobile}
            disabled={optionsToUseMobile.length === 0}
            defaultValue={defaultOption?.value}
          />
        </Flex>
      </Box>
    );
  },
);

export default NewsSelector;
